/**
 * Version Notes Generator
 *
 * Generates human-readable version notes by comparing original and updated asset data.
 * This helps users understand what changed in each version.
 */

import { Asset } from '../types/asset.types';

interface ChangesSummary {
  changes: string[];
  versionNotes: string;
}

/**
 * Generates version notes by detecting what changed between original and updated data
 */
export function generateVersionNotes(
  originalAsset: Asset | null,
  updateData: any
): string {
  if (!originalAsset) {
    return 'Initial version';
  }

  const changes: string[] = [];

  // Check creator description
  // CRITICAL FIX: For G layer, creatorDescription is in songMetadata, not root level
  const originalCreatorDesc = originalAsset.layer === 'G'
    ? (originalAsset.songMetadata as any)?.creatorDescription
    : originalAsset.creatorDescription;
  const updatedCreatorDesc = originalAsset.layer === 'G'
    ? updateData.songMetadata?.creatorDescription
    : updateData.creatorDescription;

  if (updatedCreatorDesc !== undefined &&
      updatedCreatorDesc !== originalCreatorDesc) {
    changes.push('Updated creator description');
  }

  // Check AI description
  if (updateData.description !== undefined &&
      updateData.description !== originalAsset.description) {
    changes.push('Updated AI description');
  }

  // Check tags
  if (updateData.tags !== undefined) {
    const originalTags = originalAsset.tags || [];
    const newTags = updateData.tags || [];

    const added = newTags.filter((t: string) => !originalTags.includes(t));
    const removed = originalTags.filter((t: string) => !newTags.includes(t));

    if (added.length > 0) {
      changes.push(`Added tag${added.length > 1 ? 's' : ''}: ${added.join(', ')}`);
    }
    if (removed.length > 0) {
      changes.push(`Removed tag${removed.length > 1 ? 's' : ''}: ${removed.join(', ')}`);
    }
  }

  // Check song metadata
  if (updateData.songMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.songMetadata || {},
      updateData.songMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated song metadata (${fields.join(', ')})`);
    }
  }

  // Check star metadata
  if (updateData.starMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.starMetadata || {},
      updateData.starMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated star metadata (${fields.join(', ')})`);
    }
  }

  // Check looks metadata
  if (updateData.looksMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.looksMetadata || {},
      updateData.looksMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated looks metadata (${fields.join(', ')})`);
    }
  }

  // Check moves metadata
  if (updateData.movesMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.movesMetadata || {},
      updateData.movesMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated moves metadata (${fields.join(', ')})`);
    }
  }

  // Check worlds metadata
  if (updateData.worldsMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.worldsMetadata || {},
      updateData.worldsMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated worlds metadata (${fields.join(', ')})`);
    }
  }

  // Check AlgoRhythm metadata (recommendation engine fields)
  if (updateData.algorhythmMetadata !== undefined) {
    const fields = detectMetadataChanges(
      originalAsset.algorhythmMetadata || {},
      updateData.algorhythmMetadata
    );
    if (fields.length > 0) {
      changes.push(`Updated AlgoRhythm metadata (${fields.join(', ')})`);
    }
  }

  // If no specific changes detected, return generic note
  if (changes.length === 0) {
    return 'Asset updated';
  }

  // Return human-readable summary
  return changes.join('; ');
}

/**
 * Detects which metadata fields changed
 */
function detectMetadataChanges(
  original: Record<string, any>,
  updated: Record<string, any>
): string[] {
  const changedFields: string[] = [];
  const significantFields = [
    'creatorDescription', // Added: Track creator description changes in metadata
    'songName',
    'artistName',
    'albumName',
    'albumArt',
    'albumArtUrl',
    'bpm',
    'genre',
    'hairColor',
    'hairStyle',
    'eyeColor',
    'skinTone',
    'outfit',
    'brandNames',
    'primaryColor',
    'danceStyle',
    'difficulty',
    'setting',
    'timeOfDay'
  ];

  // Check traditional metadata fields
  for (const field of significantFields) {
    if (updated[field] !== undefined && updated[field] !== original[field]) {
      // Convert camelCase to readable format
      const readable = field.replace(/([A-Z])/g, ' $1').toLowerCase();
      changedFields.push(readable);
    }
  }

  // CRITICAL FIX: Check AlgoRhythm metadata fields (arrays)
  const algorhythmArrayFields = [
    'performanceContext',
    'targetAudience',
    'culturalContext',
    'musicalStyle',
    'ageAppropriateness'  // Added: Age Appropriateness is also an AlgoRhythm field
  ];

  for (const field of algorhythmArrayFields) {
    if (updated[field] !== undefined) {
      const originalArray = original[field] || [];
      const updatedArray = updated[field] || [];

      // Compare arrays by converting to sorted strings
      const originalStr = JSON.stringify([...originalArray].sort());
      const updatedStr = JSON.stringify([...updatedArray].sort());

      if (originalStr !== updatedStr) {
        // Convert camelCase to readable format
        const readable = field.replace(/([A-Z])/g, ' $1').toLowerCase();

        // CRITICAL FIX: Show both additions and removals
        const added = updatedArray.filter((item: string) => !originalArray.includes(item));
        const removed = originalArray.filter((item: string) => !updatedArray.includes(item));

        const changes: string[] = [];
        if (added.length > 0) {
          changes.push(`added ${added.join(', ')}`);
        }
        if (removed.length > 0) {
          changes.push(`removed ${removed.join(', ')}`);
        }

        if (changes.length > 0) {
          changedFields.push(`${readable}: ${changes.join('; ')}`);
        }
      }
    }
  }

  // Check energy level (single value)
  if (updated.energyLevel !== undefined && updated.energyLevel !== original.energyLevel) {
    changedFields.push(`energy level: ${updated.energyLevel}`);
  }

  return changedFields.slice(0, 5); // Increased limit to 5 to show AlgoRhythm changes
}

/**
 * Generates a detailed changelog for version comparison
 */
export function generateDetailedChangelog(
  originalAsset: Asset | null,
  updatedAsset: Asset
): ChangesSummary {
  const changes: string[] = [];

  if (!originalAsset) {
    return {
      changes: ['Initial version created'],
      versionNotes: 'Initial version'
    };
  }

  // Compare all significant fields
  if (updatedAsset.creatorDescription !== originalAsset.creatorDescription) {
    changes.push(`Creator Description: "${originalAsset.creatorDescription?.substring(0, 50)}..." → "${updatedAsset.creatorDescription?.substring(0, 50)}..."`);
  }

  if (updatedAsset.description !== originalAsset.description) {
    changes.push(`AI Description: Updated`);
  }

  // Tags comparison
  const originalTags = originalAsset.tags || [];
  const newTags = updatedAsset.tags || [];
  if (JSON.stringify(originalTags.sort()) !== JSON.stringify(newTags.sort())) {
    const added = newTags.filter(t => !originalTags.includes(t));
    const removed = originalTags.filter(t => !newTags.includes(t));

    if (added.length > 0) {
      changes.push(`Tags Added: ${added.join(', ')}`);
    }
    if (removed.length > 0) {
      changes.push(`Tags Removed: ${removed.join(', ')}`);
    }
  }

  return {
    changes,
    versionNotes: changes.length > 0 ? changes.join('; ') : 'Asset updated'
  };
}
